import React, { useState, useEffect } from 'react';
import { 
  Inbox, 
  Bug, 
  Lightbulb, 
  MessageSquare, 
  RefreshCw, 
  Search, 
  Filter, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Trash2, 
  Mail, 
  ExternalLink, 
  Copy, 
  Check, 
  Laptop, 
  ChevronDown, 
  ChevronUp, 
  Download, 
  ShieldCheck, 
  Sparkles,
  Loader2,
  AlertCircle,
  Landmark,
  Building2,
  Phone,
  User,
  Users,
  Calendar,
  Zap,
  PhoneCall,
  Presentation
} from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { logAuditAction } from '../utils/auditLogger';
import { downloadFile } from './ImportExportCenter';
import { DemoRequestData } from './RequestDemoModal';
import { PitchDeckModal } from './PitchDeckModal';

export interface FeedbackReportItem {
  id?: string;
  ticketId: string;
  type: 'bug' | 'feature' | 'feedback';
  title: string;
  description: string;
  severity?: 'low' | 'medium' | 'high' | 'critical' | 'n/a';
  userEmail: string;
  diagnostics?: {
    appVersion?: string;
    currentView?: string;
    browser?: string;
    os?: string;
    screenResolution?: string;
    timestamp?: string;
    userEmail?: string;
  } | null;
  createdAt: string;
  status: 'new' | 'in_progress' | 'resolved';
}

interface FounderSupportAdminHubProps {
  currentUserEmail?: string | null;
  showToast: (msg: string, type?: 'info' | 'success') => void;
}

function FounderSupportAdminHubComponent({
  currentUserEmail,
  showToast
}: FounderSupportAdminHubProps) {
  // Main Tab Navigation: Support Tickets vs Demo Requests
  const [activeMainTab, setActiveMainTab] = useState<'demos' | 'support'>('demos');

  // Support Reports State
  const [reports, setReports] = useState<FeedbackReportItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'bug' | 'feature' | 'feedback' | 'resolved'>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [copiedTicketId, setCopiedTicketId] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);

  // Demo Requests State
  const [demoRequests, setDemoRequests] = useState<DemoRequestData[]>([]);
  const [demoSearchQuery, setDemoSearchQuery] = useState<string>('');
  const [demoTypeFilter, setDemoTypeFilter] = useState<string>('all');
  const [demoStatusFilter, setDemoStatusFilter] = useState<string>('all');
  const [isUpdatingDemoStatus, setIsUpdatingDemoStatus] = useState<string | null>(null);

  // Pitch Deck Modal State
  const [pitchDeckOpen, setPitchDeckOpen] = useState<boolean>(false);

  // 1. Live Sync from Firestore for Feedback Reports + Local Queue Fallback
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const loadLocalFallback = () => {
      try {
        const local = JSON.parse(localStorage.getItem('azuraos_local_feedback_queue') || '[]');
        return Array.isArray(local) ? local : [];
      } catch {
        return [];
      }
    };

    try {
      const colRef = collection(db, 'feedback_reports');
      unsubscribe = onSnapshot(
        colRef,
        (snapshot) => {
          const firestoreItems: FeedbackReportItem[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data() as FeedbackReportItem;
            firestoreItems.push({
              ...data,
              id: docSnap.id,
              ticketId: data.ticketId || docSnap.id
            });
          });

          const localItems = loadLocalFallback();
          const mergedMap = new Map<string, FeedbackReportItem>();

          firestoreItems.forEach((item) => mergedMap.set(item.ticketId, item));
          localItems.forEach((item: FeedbackReportItem) => {
            if (!mergedMap.has(item.ticketId)) {
              mergedMap.set(item.ticketId, item);
            }
          });

          const sorted = Array.from(mergedMap.values()).sort(
            (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );

          setReports(sorted);
          setIsLoading(false);
        },
        (error) => {
          console.warn('Firestore real-time feedback listener notice (loading local fallback):', error);
          const localItems = loadLocalFallback();
          setReports(localItems);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.warn('Could not initialize feedback reports snapshot listener:', err);
      const localItems = loadLocalFallback();
      setReports(localItems);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // 2. Live Sync from Firestore for Demo & Pilot Requests
  useEffect(() => {
    let unsubscribeDemos: (() => void) | undefined;

    const loadLocalDemoQueue = () => {
      try {
        const local = JSON.parse(localStorage.getItem('azuraos_demo_requests_queue') || '[]');
        return Array.isArray(local) ? local : [];
      } catch {
        return [];
      }
    };

    try {
      const colRef = collection(db, 'demo_requests');
      unsubscribeDemos = onSnapshot(
        colRef,
        (snapshot) => {
          const firestoreItems: DemoRequestData[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data() as DemoRequestData;
            firestoreItems.push({
              ...data,
              requestId: data.requestId || docSnap.id
            });
          });

          const localItems = loadLocalDemoQueue();
          const mergedMap = new Map<string, DemoRequestData>();

          firestoreItems.forEach((item) => mergedMap.set(item.requestId, item));
          localItems.forEach((item: DemoRequestData) => {
            if (!mergedMap.has(item.requestId)) {
              mergedMap.set(item.requestId, item);
            }
          });

          const sorted = Array.from(mergedMap.values()).sort(
            (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );

          setDemoRequests(sorted);
        },
        (error) => {
          console.warn('Firestore demo requests listener note (using local queue):', error);
          setDemoRequests(loadLocalDemoQueue());
        }
      );
    } catch (err) {
      console.warn('Could not initialize demo requests listener:', err);
      setDemoRequests(loadLocalDemoQueue());
    }

    return () => {
      if (unsubscribeDemos) unsubscribeDemos();
    };
  }, []);

  // Update Status for Demo Request
  const handleUpdateDemoStatus = async (requestId: string, newStatus: 'new' | 'contacted' | 'scheduled' | 'completed') => {
    setIsUpdatingDemoStatus(requestId);
    try {
      try {
        const docRef = doc(db, 'demo_requests', requestId);
        await updateDoc(docRef, { status: newStatus });
      } catch (err) {
        console.warn('Firestore demo status update notice:', err);
      }

      setDemoRequests((prev) =>
        prev.map((d) => (d.requestId === requestId ? { ...d, status: newStatus } : d))
      );

      const local = JSON.parse(localStorage.getItem('azuraos_demo_requests_queue') || '[]');
      const updatedLocal = local.map((d: DemoRequestData) =>
        d.requestId === requestId ? { ...d, status: newStatus } : d
      );
      localStorage.setItem('azuraos_demo_requests_queue', JSON.stringify(updatedLocal));

      logAuditAction({
        actionType: 'UPDATE_SETTINGS',
        entityType: 'System',
        entityId: requestId,
        details: `Updated Demo request [${requestId}] status to ${newStatus.toUpperCase()}`,
        performedBy: currentUserEmail || 'workspace-admin'
      });

      showToast(`Updated demo #${requestId} to ${newStatus.toUpperCase()}`, 'success');
    } catch (err) {
      console.error('Failed to update demo status:', err);
      showToast('⚠️ Failed to update demo status.', 'info');
    } finally {
      setIsUpdatingDemoStatus(null);
    }
  };

  // Delete Demo Request
  const handleDeleteDemoRequest = async (requestId: string) => {
    if (!window.confirm(`Are you sure you want to delete demo request #${requestId}?`)) {
      return;
    }

    try {
      try {
        const docRef = doc(db, 'demo_requests', requestId);
        await deleteDoc(docRef);
      } catch (err) {
        console.warn('Firestore demo delete notice:', err);
      }

      setDemoRequests((prev) => prev.filter((d) => d.requestId !== requestId));

      const local = JSON.parse(localStorage.getItem('azuraos_demo_requests_queue') || '[]');
      const updatedLocal = local.filter((d: DemoRequestData) => d.requestId !== requestId);
      localStorage.setItem('azuraos_demo_requests_queue', JSON.stringify(updatedLocal));

      showToast(`🗑️ Demo request #${requestId} deleted.`, 'info');
    } catch (err) {
      console.error('Failed to delete demo request:', err);
      showToast('⚠️ Failed to delete demo request.', 'info');
    }
  };

  // Direct WhatsApp Link to Prospect
  const handleOpenWhatsApp = (phone: string, fullName: string, companyName: string) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const text = encodeURIComponent(
      `Hi ${fullName}, this is the founder of AzuraOS following up on your demo/pilot request for ${companyName}. When is a good time for a quick 15-min screen share?`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${text}`, '_blank');
  };

  // Export Demo Requests as CSV
  const handleExportDemoCSV = () => {
    if (demoRequests.length === 0) {
      showToast('⚠️ No demo requests available to export.', 'info');
      return;
    }

    const headers = [
      'Request ID',
      'Full Name',
      'Work Email',
      'Company Name',
      'Phone / WhatsApp',
      'Inquiry Type',
      'Cohort Size',
      'Focus Area',
      'Preferred Time',
      'Status',
      'Created At',
      'Notes'
    ];

    const rows = demoRequests.map((d) => [
      d.requestId,
      `"${(d.fullName || '').replace(/"/g, '""')}"`,
      d.workEmail,
      `"${(d.companyName || '').replace(/"/g, '""')}"`,
      d.phone,
      d.inquiryType,
      `"${(d.cohortSize || '').replace(/"/g, '""')}"`,
      `"${(d.focusArea || '').replace(/"/g, '""')}"`,
      `"${(d.preferredDateTime || '').replace(/"/g, '""')}"`,
      d.status,
      d.createdAt,
      `"${(d.notes || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    downloadFile(
      csvContent,
      `azuraos_demo_leads_${new Date().toISOString().split('T')[0]}.csv`,
      'text/csv'
    );
    showToast('📥 Demo & pilot leads exported as CSV!', 'success');
  };

  // Update Status in Firestore and Local Queue for Support Tickets
  const handleUpdateStatus = async (ticketId: string, newStatus: 'new' | 'in_progress' | 'resolved') => {
    setIsUpdatingStatus(ticketId);
    try {
      try {
        const docRef = doc(db, 'feedback_reports', ticketId);
        await updateDoc(docRef, { status: newStatus });
      } catch (err) {
        console.warn('Firestore status update note:', err);
      }

      setReports((prev) =>
        prev.map((r) => (r.ticketId === ticketId ? { ...r, status: newStatus } : r))
      );

      const local = JSON.parse(localStorage.getItem('azuraos_local_feedback_queue') || '[]');
      const updatedLocal = local.map((r: FeedbackReportItem) =>
        r.ticketId === ticketId ? { ...r, status: newStatus } : r
      );
      localStorage.setItem('azuraos_local_feedback_queue', JSON.stringify(updatedLocal));

      logAuditAction({
        actionType: 'UPDATE_SETTINGS',
        entityType: 'System',
        entityId: ticketId,
        details: `Updated feedback ticket [${ticketId}] status to ${newStatus.toUpperCase()}`,
        performedBy: currentUserEmail || 'workspace-admin'
      });

      showToast(`Updated #${ticketId} to ${newStatus.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Failed to update ticket status:', error);
      showToast('⚠️ Failed to update ticket status.', 'info');
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  // Delete Support Ticket
  const handleDeleteTicket = async (ticketId: string) => {
    if (!window.confirm(`Are you sure you want to delete ticket #${ticketId}? This action cannot be undone.`)) {
      return;
    }

    try {
      try {
        const docRef = doc(db, 'feedback_reports', ticketId);
        await deleteDoc(docRef);
      } catch (err) {
        console.warn('Firestore delete doc notice:', err);
      }

      setReports((prev) => prev.filter((r) => r.ticketId !== ticketId));

      const local = JSON.parse(localStorage.getItem('azuraos_local_feedback_queue') || '[]');
      const updatedLocal = local.filter((r: FeedbackReportItem) => r.ticketId !== ticketId);
      localStorage.setItem('azuraos_local_feedback_queue', JSON.stringify(updatedLocal));

      logAuditAction({
        actionType: 'DELETE_EXPENSE',
        entityType: 'System',
        entityId: ticketId,
        details: `Deleted feedback ticket [${ticketId}]`,
        performedBy: currentUserEmail || 'workspace-admin'
      });

      showToast(`🗑️ Ticket #${ticketId} deleted.`, 'info');
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      showToast('⚠️ Failed to delete ticket.', 'info');
    }
  };

  // Copy Ticket Details
  const handleCopyTicketDetails = (report: FeedbackReportItem) => {
    const text = `--- AzuraOS Ticket [${report.ticketId}] ---
Type: ${report.type.toUpperCase()}
Title: ${report.title}
Status: ${report.status.toUpperCase()}
Severity: ${report.severity || 'N/A'}
User: ${report.userEmail || 'Anonymous'}
Date: ${report.createdAt}
Description:
${report.description}
${
  report.diagnostics
    ? `\n--- Diagnostics ---\nRoute: ${report.diagnostics.currentView}\nOS: ${report.diagnostics.os}\nBrowser: ${report.diagnostics.browser}\nScreen: ${report.diagnostics.screenResolution}\nVersion: ${report.diagnostics.appVersion}`
    : ''
}`;

    navigator.clipboard.writeText(text);
    setCopiedTicketId(report.ticketId);
    showToast(`Copied #${report.ticketId} details to clipboard.`, 'success');
    setTimeout(() => setCopiedTicketId(null), 2000);
  };

  // 1-Click Direct Reply from support@azuraos.app
  const handleReplyToUser = (report: FeedbackReportItem) => {
    if (!report.userEmail || report.userEmail === 'anonymous@user') {
      showToast('⚠️ No submitter email provided for this ticket.', 'info');
      return;
    }
    const subject = encodeURIComponent(`Re: [${report.ticketId}] AzuraOS Support — ${report.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nThank you for submitting ticket #${report.ticketId} regarding: "${report.title}".\n\n[Your response message here]\n\nBest regards,\nAzuraOS Founder & Engineering Team\nsupport@azuraos.app`
    );
    window.open(`mailto:${report.userEmail}?cc=support@azuraos.app&subject=${subject}&body=${body}`, '_blank');
  };

  // Export Tickets as CSV
  const handleExportCSV = () => {
    if (reports.length === 0) {
      showToast('⚠️ No tickets available to export.', 'info');
      return;
    }

    const headers = [
      'Ticket ID',
      'Type',
      'Status',
      'Severity',
      'Title',
      'User Email',
      'Created At',
      'Description',
      'Current View',
      'OS',
      'Browser'
    ];

    const rows = reports.map((r) => [
      r.ticketId,
      r.type,
      r.status,
      r.severity || 'n/a',
      `"${(r.title || '').replace(/"/g, '""')}"`,
      r.userEmail || 'Anonymous',
      r.createdAt || '',
      `"${(r.description || '').replace(/"/g, '""')}"`,
      r.diagnostics?.currentView || 'N/A',
      r.diagnostics?.os || 'N/A',
      `"${(r.diagnostics?.browser || 'N/A').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    downloadFile(
      csvContent,
      `azuraos_support_tickets_${new Date().toISOString().split('T')[0]}.csv`,
      'text/csv'
    );
    showToast('📥 Support tickets exported as CSV!', 'success');
  };

  // Filtering Logic for Support Tickets
  const filteredReports = reports.filter((r) => {
    if (typeFilter === 'resolved') {
      if (r.status !== 'resolved') return false;
    } else if (typeFilter !== 'all') {
      if (r.type !== typeFilter) return false;
    }

    if (severityFilter !== 'all' && r.severity !== severityFilter) {
      return false;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (r.title || '').toLowerCase().includes(q);
      const matchDesc = (r.description || '').toLowerCase().includes(q);
      const matchEmail = (r.userEmail || '').toLowerCase().includes(q);
      const matchId = (r.ticketId || '').toLowerCase().includes(q);
      const matchRoute = (r.diagnostics?.currentView || '').toLowerCase().includes(q);
      if (!matchTitle && !matchDesc && !matchEmail && !matchId && !matchRoute) {
        return false;
      }
    }

    return true;
  });

  // Filtering Logic for Demo Requests
  const filteredDemoRequests = demoRequests.filter((d) => {
    if (demoTypeFilter !== 'all' && d.inquiryType !== demoTypeFilter) {
      return false;
    }
    if (demoStatusFilter !== 'all' && d.status !== demoStatusFilter) {
      return false;
    }
    if (demoSearchQuery.trim()) {
      const q = demoSearchQuery.toLowerCase();
      const matchName = (d.fullName || '').toLowerCase().includes(q);
      const matchCompany = (d.companyName || '').toLowerCase().includes(q);
      const matchEmail = (d.workEmail || '').toLowerCase().includes(q);
      const matchId = (d.requestId || '').toLowerCase().includes(q);
      const matchPhone = (d.phone || '').toLowerCase().includes(q);
      if (!matchName && !matchCompany && !matchEmail && !matchId && !matchPhone) {
        return false;
      }
    }
    return true;
  });

  // Metrics for Support Reports
  const totalCount = reports.length;
  const bugCount = reports.filter((r) => r.type === 'bug' && r.status !== 'resolved').length;
  const featureCount = reports.filter((r) => r.type === 'feature' && r.status !== 'resolved').length;
  const feedbackCount = reports.filter((r) => r.type === 'feedback' && r.status !== 'resolved').length;
  const resolvedCount = reports.filter((r) => r.status === 'resolved').length;
  const criticalBugs = reports.filter((r) => r.type === 'bug' && r.severity === 'critical' && r.status !== 'resolved').length;

  // Metrics for Demo Requests
  const totalDemoCount = demoRequests.length;
  const newDemoCount = demoRequests.filter((d) => d.status === 'new').length;
  const bankPilots = demoRequests.filter((d) => d.inquiryType === 'bank_pilot').length;
  const enterpriseSponsors = demoRequests.filter((d) => d.inquiryType === 'enterprise_sponsor').length;
  const vip1on1Count = demoRequests.filter((d) => d.inquiryType === 'vip_1on1').length;

  return (
    <div className="space-y-6">
      
      {/* Top Banner & Quick Metrics */}
      <div className="p-6 bg-gradient-to-r from-teal-500/10 via-[#151819] to-cyan-500/5 border border-[#14B8A6]/30 rounded-2xl shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center shrink-0 shadow-inner">
              <ShieldCheck size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white tracking-tight">Super Admin Command Center</h2>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-[#14B8A6]/20 text-[#14B8A6] rounded border border-[#14B8A6]/40">
                  DUAL-SYNC INBOX
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 rounded border border-amber-500/40">
                  FOUNDER ACCESS
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                Real-time command center for <strong className="text-teal-300">VIP Demos &amp; Institutional Pilot Leads</strong> and <strong className="text-gray-200">Customer Support Tickets</strong>.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setPitchDeckOpen(true)}
              className="px-3.5 py-2 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 hover:from-teal-500/30 hover:to-emerald-500/30 text-teal-300 font-extrabold text-xs rounded-xl border border-teal-500/40 transition flex items-center gap-2 cursor-pointer shadow-md"
              title="Open Institutional 10-Slide Pitch Deck (PDF & Print ready)"
            >
              <Presentation size={14} className="text-teal-300" />
              <span>10-Slide Pitch Deck</span>
            </button>

            {activeMainTab === 'demos' ? (
              <button
                onClick={handleExportDemoCSV}
                className="px-3.5 py-2 bg-[#0C0E0F] hover:bg-[#1A1D1E] text-teal-300 hover:text-white font-bold text-xs rounded-xl border border-teal-500/30 transition flex items-center gap-2 cursor-pointer shadow-sm"
                title="Export all demo leads to CSV"
              >
                <Download size={13} />
                <span>Export Leads CSV</span>
              </button>
            ) : (
              <button
                onClick={handleExportCSV}
                className="px-3.5 py-2 bg-[#0C0E0F] hover:bg-[#1A1D1E] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-2 cursor-pointer shadow-sm"
                title="Export all tickets to CSV"
              >
                <Download size={13} />
                <span>Export Tickets CSV</span>
              </button>
            )}
          </div>
        </div>

        {/* Primary View Switcher: Demos vs Support */}
        <div className="flex items-center gap-2 pt-2 border-t border-[#1F2426]">
          <button
            onClick={() => setActiveMainTab('demos')}
            className={`px-4 py-2 rounded-xl text-xs font-extrabold transition flex items-center gap-2 cursor-pointer ${
              activeMainTab === 'demos'
                ? 'bg-[#14B8A6] text-black shadow-lg shadow-[#14B8A6]/20'
                : 'bg-[#0C0E0F] text-gray-400 hover:text-white border border-[#2A2F31]'
            }`}
          >
            <Sparkles size={14} />
            <span>VIP Demos &amp; Pilot Leads</span>
            <span className={`px-2 py-0.2 text-[10px] rounded-full font-mono font-bold ${
              activeMainTab === 'demos' ? 'bg-black/20 text-black' : 'bg-[#1F2426] text-teal-400'
            }`}>
              {totalDemoCount}
            </span>
          </button>

          <button
            onClick={() => setActiveMainTab('support')}
            className={`px-4 py-2 rounded-xl text-xs font-extrabold transition flex items-center gap-2 cursor-pointer ${
              activeMainTab === 'support'
                ? 'bg-[#14B8A6] text-black shadow-lg shadow-[#14B8A6]/20'
                : 'bg-[#0C0E0F] text-gray-400 hover:text-white border border-[#2A2F31]'
            }`}
          >
            <Inbox size={14} />
            <span>Support &amp; Bug Tickets</span>
            <span className={`px-2 py-0.2 text-[10px] rounded-full font-mono font-bold ${
              activeMainTab === 'support' ? 'bg-black/20 text-black' : 'bg-[#1F2426] text-gray-300'
            }`}>
              {totalCount}
            </span>
          </button>
        </div>

        {/* Live Metrics Grid */}
        {activeMainTab === 'demos' ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <div className="p-3 bg-[#0C0E0F]/80 border border-teal-500/30 rounded-xl">
              <div className="text-[10px] text-teal-400 font-bold uppercase tracking-wider flex items-center justify-between">
                <span>Total Leads</span>
                {newDemoCount > 0 && (
                  <span className="px-1.5 py-0.2 rounded text-[8px] bg-teal-500 text-black font-mono font-bold">
                    {newDemoCount} NEW
                  </span>
                )}
              </div>
              <div className="text-lg font-extrabold text-teal-300 font-mono mt-0.5">{totalDemoCount}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-cyan-500/30 rounded-xl">
              <div className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">Bank &amp; SME Pilots</div>
              <div className="text-lg font-extrabold text-cyan-300 font-mono mt-0.5">{bankPilots}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-amber-500/30 rounded-xl">
              <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">ESD &amp; Sponsors</div>
              <div className="text-lg font-extrabold text-amber-300 font-mono mt-0.5">{enterpriseSponsors}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">VIP 1-on-1 Demos</div>
              <div className="text-lg font-extrabold text-white font-mono mt-0.5">{vip1on1Count}</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
            <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Tickets</div>
              <div className="text-lg font-extrabold text-white font-mono mt-0.5">{totalCount}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-rose-500/30 rounded-xl">
              <div className="text-[10px] text-rose-400 font-bold uppercase tracking-wider flex items-center justify-between">
                <span>Open Bugs</span>
                {criticalBugs > 0 && (
                  <span className="px-1.5 py-0.2 rounded text-[8px] bg-rose-500 text-white font-mono font-bold animate-pulse">
                    {criticalBugs} CRIT
                  </span>
                )}
              </div>
              <div className="text-lg font-extrabold text-rose-400 font-mono mt-0.5">{bugCount}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-amber-500/30 rounded-xl">
              <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">Feature Requests</div>
              <div className="text-lg font-extrabold text-amber-400 font-mono mt-0.5">{featureCount}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-teal-500/30 rounded-xl">
              <div className="text-[10px] text-teal-400 font-bold uppercase tracking-wider">General Feedback</div>
              <div className="text-lg font-extrabold text-teal-400 font-mono mt-0.5">{feedbackCount}</div>
            </div>

            <div className="p-3 bg-[#0C0E0F]/80 border border-emerald-500/30 rounded-xl">
              <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">Resolved</div>
              <div className="text-lg font-extrabold text-emerald-400 font-mono mt-0.5">{resolvedCount}</div>
            </div>
          </div>
        )}
      </div>

      {/* VIEW 1: DEMO & PILOT LEADS */}
      {activeMainTab === 'demos' && (
        <div className="p-5 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            
            {/* Filter Archetype Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full">
              {[
                { id: 'all', label: `All Leads (${totalDemoCount})` },
                { id: 'vip_1on1', label: `VIP 1-on-1 (${vip1on1Count})`, icon: User, color: 'text-teal-400' },
                { id: 'bank_pilot', label: `Bank Pilot (${bankPilots})`, icon: Landmark, color: 'text-cyan-400' },
                { id: 'enterprise_sponsor', label: `ESD / Sponsor (${enterpriseSponsors})`, icon: Building2, color: 'text-amber-400' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDemoTypeFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    demoTypeFilter === tab.id
                      ? 'bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/40 shadow-sm'
                      : 'bg-[#0C0E0F] text-gray-400 hover:text-white border border-[#2A2F31]'
                  }`}
                >
                  {tab.icon && <tab.icon size={13} className={tab.color} />}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Status Filter & Search */}
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <select
                value={demoStatusFilter}
                onChange={(e) => setDemoStatusFilter(e.target.value)}
                className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-2.5 py-2 text-xs text-gray-300 font-bold focus:outline-none focus:border-[#14B8A6]"
              >
                <option value="all">All Pipeline Statuses</option>
                <option value="new">New (Uncontacted)</option>
                <option value="contacted">Contacted</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
              </select>

              <div className="relative w-full lg:w-64">
                <Search size={14} className="absolute left-3 top-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search lead, company, phone..."
                  value={demoSearchQuery}
                  onChange={(e) => setDemoSearchQuery(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] font-medium"
                />
              </div>
            </div>
          </div>

          {/* Lead Cards List */}
          {filteredDemoRequests.length === 0 ? (
            <div className="p-12 text-center bg-[#0C0E0F] border border-dashed border-[#2A2F31] rounded-2xl space-y-2">
              <Sparkles size={28} className="mx-auto text-gray-600 mb-1" />
              <p className="font-bold text-gray-300 text-sm">No demo or pilot requests found.</p>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                When prospective founders or banking partners request a walkthrough or cohort sponsorship, they will arrive here instantly.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDemoRequests.map((lead) => {
                const formattedDate = new Date(lead.createdAt).toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });

                return (
                  <div
                    key={lead.requestId}
                    className="p-5 bg-[#0C0E0F] border border-[#1F2426] hover:border-teal-500/40 rounded-2xl transition space-y-3.5 shadow-md"
                  >
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="text-xs font-mono font-bold text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20">
                          {lead.requestId}
                        </span>

                        <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${
                          lead.inquiryType === 'bank_pilot'
                            ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                            : lead.inquiryType === 'enterprise_sponsor'
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                            : 'bg-teal-500/20 text-teal-300 border-teal-500/30'
                        }`}>
                          {lead.inquiryType === 'bank_pilot' ? '🏦 BANK PILOT' : lead.inquiryType === 'enterprise_sponsor' ? '🏛️ ESD SPONSOR' : '🎯 VIP 1-ON-1'}
                        </span>

                        <span className="text-xs text-gray-400 font-mono">
                          {formattedDate}
                        </span>
                      </div>

                      {/* Pipeline Status Selector */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-500 uppercase font-mono">Status:</span>
                        <select
                          value={lead.status}
                          disabled={isUpdatingDemoStatus === lead.requestId}
                          onChange={(e) => handleUpdateDemoStatus(lead.requestId, e.target.value as any)}
                          className="bg-[#151819] border border-[#2A2F31] text-xs font-bold rounded-lg px-2.5 py-1 text-white focus:outline-none focus:border-[#14B8A6] cursor-pointer"
                        >
                          <option value="new">🟡 New</option>
                          <option value="contacted">🔵 Contacted</option>
                          <option value="scheduled">🟢 Scheduled</option>
                          <option value="completed">🟣 Completed</option>
                        </select>

                        <button
                          onClick={() => handleDeleteDemoRequest(lead.requestId)}
                          className="p-1 text-gray-500 hover:text-rose-400 rounded transition cursor-pointer"
                          title="Delete Lead"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Prospect Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 bg-[#151819] p-3.5 rounded-xl border border-[#2A2F31] text-xs">
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-mono block">Contact Person</span>
                        <div className="font-bold text-white flex items-center gap-1.5 mt-0.5">
                          <User size={13} className="text-teal-400" />
                          <span>{lead.fullName}</span>
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-mono block">Company / Organization</span>
                        <div className="font-bold text-white flex items-center gap-1.5 mt-0.5">
                          <Building2 size={13} className="text-teal-400" />
                          <span>{lead.companyName}</span>
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-mono block">Cohort Size</span>
                        <div className="font-semibold text-gray-300 flex items-center gap-1.5 mt-0.5">
                          <Users size={13} className="text-teal-400" />
                          <span>{lead.cohortSize}</span>
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-mono block">Preferred Time</span>
                        <div className="font-semibold text-gray-300 flex items-center gap-1.5 mt-0.5">
                          <Clock size={13} className="text-teal-400" />
                          <span>{lead.preferredDateTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Focus Area & Notes */}
                    <div className="space-y-1 text-xs">
                      <div className="text-[11px] font-bold text-teal-300 flex items-center gap-1.5">
                        <Zap size={12} />
                        <span>Focus: {lead.focusArea}</span>
                      </div>
                      {lead.notes && (
                        <p className="text-gray-400 bg-[#151819] p-2.5 rounded-lg border border-[#2A2F31] leading-relaxed">
                          "{lead.notes}"
                        </p>
                      )}
                    </div>

                    {/* Quick Response Actions */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                      <div className="flex items-center gap-2">
                        {/* WhatsApp Quick Reply */}
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsApp(lead.phone, lead.fullName, lead.companyName)}
                          className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-lg border border-emerald-500/30 transition flex items-center gap-1.5 cursor-pointer"
                        >
                          <PhoneCall size={13} />
                          <span>Chat on WhatsApp ({lead.phone})</span>
                          <ExternalLink size={11} />
                        </button>

                        {/* Email Reply */}
                        <button
                          type="button"
                          onClick={() => {
                            const subject = encodeURIComponent(`[${lead.requestId}] AzuraOS Demo Walkthrough — ${lead.companyName}`);
                            const body = encodeURIComponent(
                              `Hi ${lead.fullName},\n\nThank you for requesting a VIP walkthrough of AzuraOS for ${lead.companyName}.\n\nI would love to connect and demonstrate our live margin defense engine and operator workflows.\n\nLooking forward to speaking with you!\n\nBest regards,\nAzuraOS Founder & Engineering\nsupport@azuraos.app`
                            );
                            window.location.href = `mailto:${lead.workEmail}?subject=${subject}&body=${body}`;
                          }}
                          className="px-3 py-1.5 bg-[#151819] hover:bg-[#1F2426] text-gray-300 hover:text-white text-xs font-bold rounded-lg border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                        >
                          <Mail size={13} />
                          <span>Email ({lead.workEmail})</span>
                        </button>
                      </div>

                      <div className="text-[10px] text-gray-500 font-mono">
                        Saved in Secure Cloud Queue
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: SUPPORT & BUG TICKETS */}
      {activeMainTab === 'support' && (
        <div className="p-5 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            
            {/* Category Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full">
              {[
                { id: 'all', label: `All Tickets (${totalCount})` },
                { id: 'bug', label: `Bugs (${bugCount})`, icon: Bug, color: 'text-rose-400' },
                { id: 'feature', label: `Features (${featureCount})`, icon: Lightbulb, color: 'text-amber-400' },
                { id: 'feedback', label: `Feedback (${feedbackCount})`, icon: MessageSquare, color: 'text-teal-400' },
                { id: 'resolved', label: `Resolved (${resolvedCount})`, icon: CheckCircle2, color: 'text-emerald-400' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setTypeFilter(tab.id as any)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    typeFilter === tab.id
                      ? 'bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/40 shadow-sm'
                      : 'bg-[#0C0E0F] text-gray-400 hover:text-white border border-[#2A2F31]'
                  }`}
                >
                  {tab.icon && <tab.icon size={13} className={tab.color} />}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Search Input & Severity Filter */}
            <div className="flex items-center gap-2 w-full lg:w-auto">
              {typeFilter === 'bug' && (
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-2.5 py-2 text-xs text-gray-300 font-bold focus:outline-none focus:border-[#14B8A6]"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              )}

              <div className="relative w-full lg:w-64">
                <Search size={14} className="absolute left-3 top-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search ticket, title, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6] font-medium"
                />
              </div>
            </div>
          </div>

          {/* Ticket List */}
          {isLoading ? (
            <div className="p-12 text-center text-gray-400 space-y-2">
              <Loader2 size={24} className="animate-spin mx-auto text-[#14B8A6]" />
              <p className="text-xs">Loading feedback tickets from Firestore queue...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="p-12 text-center bg-[#0C0E0F] border border-dashed border-[#2A2F31] rounded-2xl space-y-2">
              <Inbox size={28} className="mx-auto text-gray-600 mb-1" />
              <p className="font-bold text-gray-300 text-sm">No tickets found in this view.</p>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                When users report bugs or submit feedback, their reports will appear here in real-time with full device specs and direct reply options.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReports.map((report) => {
                const isExpanded = expandedTicketId === report.ticketId;
                const formattedDate = new Date(report.createdAt).toLocaleDateString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={report.ticketId}
                    className={`bg-[#0C0E0F] border rounded-2xl transition overflow-hidden ${
                      report.status === 'resolved'
                        ? 'border-[#1F2426] opacity-75'
                        : report.severity === 'critical'
                        ? 'border-rose-500/50 shadow-lg shadow-rose-950/20'
                        : 'border-[#1F2426] hover:border-gray-700'
                    }`}
                  >
                    {/* Collapsed Header Bar */}
                    <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 border ${
                            report.type === 'bug'
                              ? 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                              : report.type === 'feature'
                              ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                              : 'bg-teal-500/10 border-teal-500/30 text-teal-400'
                          }`}
                        >
                          {report.type === 'bug' ? (
                            <Bug size={18} />
                          ) : report.type === 'feature' ? (
                            <Lightbulb size={18} />
                          ) : (
                            <MessageSquare size={18} />
                          )}
                        </div>

                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] font-mono font-bold text-gray-400 bg-[#151819] px-2 py-0.5 rounded border border-[#2A2F31]">
                              {report.ticketId}
                            </span>

                            {report.severity && report.severity !== 'n/a' && (
                              <span
                                className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${
                                  report.severity === 'critical'
                                    ? 'bg-rose-500/20 text-rose-300 border-rose-500/30 animate-pulse'
                                    : report.severity === 'high'
                                    ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                                    : report.severity === 'medium'
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                    : 'bg-gray-800 text-gray-300 border-gray-700'
                                }`}
                              >
                                {report.severity}
                              </span>
                            )}

                            <span className="text-xs text-gray-500 font-mono">
                              {formattedDate}
                            </span>
                          </div>

                          <h4 className="text-sm font-bold text-white truncate">
                            {report.title}
                          </h4>

                          <p className="text-xs text-gray-400 truncate max-w-xl">
                            {report.description}
                          </p>
                        </div>
                      </div>

                      {/* Status & Quick Actions */}
                      <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                        <select
                          value={report.status}
                          disabled={isUpdatingStatus === report.ticketId}
                          onChange={(e) =>
                            handleUpdateStatus(report.ticketId, e.target.value as any)
                          }
                          className="bg-[#151819] border border-[#2A2F31] text-xs font-bold rounded-xl px-2.5 py-1.5 text-white focus:outline-none focus:border-[#14B8A6] cursor-pointer"
                        >
                          <option value="new">🟡 New</option>
                          <option value="in_progress">🔵 In Progress</option>
                          <option value="resolved">🟢 Resolved</option>
                        </select>

                        <button
                          onClick={() => handleReplyToUser(report)}
                          className="p-2 bg-[#151819] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-xl border border-[#2A2F31] transition cursor-pointer"
                          title="Direct Reply to Submitter via Email"
                        >
                          <Mail size={14} />
                        </button>

                        <button
                          onClick={() =>
                            setExpandedTicketId(isExpanded ? null : report.ticketId)
                          }
                          className="p-2 bg-[#151819] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-xl border border-[#2A2F31] transition cursor-pointer"
                          title="Expand Ticket Details & Diagnostics"
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Drawer Details */}
                    {isExpanded && (
                      <div className="p-5 border-t border-[#1F2426] bg-[#111314] space-y-4 animate-fade-in text-xs">
                        
                        <div className="space-y-1.5">
                          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider font-bold">
                            Full User Description:
                          </span>
                          <div className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl text-gray-200 leading-relaxed whitespace-pre-wrap font-sans">
                            {report.description}
                          </div>
                        </div>

                        {/* Diagnostics Matrix */}
                        {report.diagnostics && (
                          <div className="space-y-1.5">
                            <span className="text-[10px] font-mono text-teal-400 uppercase tracking-wider font-bold flex items-center gap-1.5">
                              <Laptop size={12} />
                              Client Diagnostics
                            </span>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#0C0E0F] p-3 rounded-xl border border-[#2A2F31] font-mono text-[11px]">
                              <div>
                                <span className="text-gray-500 block text-[9px]">Active View:</span>
                                <span className="text-white">{report.diagnostics.currentView || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 block text-[9px]">OS:</span>
                                <span className="text-white">{report.diagnostics.os || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 block text-[9px]">Browser:</span>
                                <span className="text-white truncate block">{report.diagnostics.browser || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 block text-[9px]">Screen:</span>
                                <span className="text-white">{report.diagnostics.screenResolution || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Submitter Info & Footer Actions */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-[#1F2426]">
                          <div className="text-gray-400 font-mono text-[11px]">
                            Submitter: <strong className="text-white">{report.userEmail || 'Anonymous'}</strong>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopyTicketDetails(report)}
                              className="px-3 py-1.5 bg-[#151819] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-lg border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                            >
                              {copiedTicketId === report.ticketId ? (
                                <Check size={12} className="text-emerald-400" />
                              ) : (
                                <Copy size={12} />
                              )}
                              <span>{copiedTicketId === report.ticketId ? 'Copied' : 'Copy All'}</span>
                            </button>

                            <button
                              onClick={() => handleDeleteTicket(report.ticketId)}
                              className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg border border-rose-500/30 transition flex items-center gap-1.5 cursor-pointer"
                            >
                              <Trash2 size={12} />
                              <span>Delete Ticket</span>
                            </button>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Institutional Pitch Deck Modal */}
      <PitchDeckModal
        isOpen={pitchDeckOpen}
        onClose={() => setPitchDeckOpen(false)}
        showToast={showToast}
        deckTitle="AzuraOS Institutional Pitch Deck"
        companyName="Azura Seraph™ Pty Ltd"
        founderName="Raksha Singh (Founder & Chief Architect)"
        contactEmail="founder@azuraos.app"
      />

    </div>
  );
}

export const FounderSupportAdminHub = React.memo(FounderSupportAdminHubComponent);

